import {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import {
  readPortfolio,
  type Portfolio,
  type PortfolioSchool,
  type SaveSchoolInput,
} from '../lib/partnerApi';
import { useAuth } from './AuthContext';

export type PortalStep =
  | 'portfolio'
  | 'manual'
  | 'import'
  | 'review'
  | 'saved';

interface PartnerSessionContextValue {
  /** The authenticated partner's display name (derived from membership). */
  partnerName: string | null;
  /** The latest saved portfolio (null if none yet). */
  portfolio: Portfolio | null;
  /** The saved portfolio schools (from the latest version). */
  savedSchools: PortfolioSchool[];
  /** The working draft of selected schools (nat_emis -> status/source). */
  draft: Map<number, SaveSchoolInput>;
  /** Loading state for the initial portfolio read. */
  loading: boolean;
  /** Error from the initial portfolio read. */
  loadError: string | null;
  /** Load the current portfolio from the server. */
  refreshPortfolio: () => Promise<void>;
  /** Add a school to the draft (manual selection). */
  addToDraft: (natEmis: number, source: 'manual' | 'spreadsheet') => void;
  /** Remove a school from the draft. */
  removeFromDraft: (natEmis: number) => void;
  /** Replace the entire draft (e.g. from an import). */
  setDraft: (schools: SaveSchoolInput[]) => void;
  /**
   * M4-R21K (D-01): Merge schools into the existing draft (additive). Importing
   * a spreadsheet or confirming match-review rows must NEVER wipe out manually
   * added draft schools. Existing draft entries are preserved; new nat_emis are
   * added; overlapping nat_emis keep their existing entry.
   */
  mergeIntoDraft: (schools: SaveSchoolInput[]) => void;
  /** Clear the draft. */
  clearDraft: () => void;
}

const PartnerSessionContext = createContext<PartnerSessionContextValue | undefined>(
  undefined,
);

/**
 * M4-R21F: Partner product state provider.
 *
 * Authorization is derived from Supabase Auth (AuthContext) — the authenticated
 * auth user → partner membership → current partner context. The legacy custom
 * bearer session (reslocate_partner_session localStorage) is REMOVED as an auth
 * authority. The edge functions resolve membership from the Supabase Auth JWT
 * via resolveAuthMembership, so this provider only needs to know whether the
 * user is authenticated to load the partner's portfolio. It holds product/draft
 * state only.
 */
export function PartnerSessionProvider({ children }: { children: ReactNode }) {
  const { status } = useAuth();
  const authenticated = status === 'authenticated';

  const [partnerName, setPartnerName] = useState<string | null>(null);
  const [portfolio, setPortfolio] = useState<Portfolio | null>(null);
  const [savedSchools, setSavedSchools] = useState<PortfolioSchool[]>([]);
  const [draft, setDraftState] = useState<Map<number, SaveSchoolInput>>(
    () => new Map(),
  );
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);

  const refreshPortfolio = async () => {
    // M4-R21F: only load when authenticated. No legacy custom session gate.
    if (!authenticated) return;
    setLoading(true);
    setLoadError(null);
    try {
      const result = await readPortfolio();
      setPartnerName(result.partnerName ?? null);
      setPortfolio(result.portfolio);
      setSavedSchools(result.schools);
    } catch (err) {
      setLoadError(err instanceof Error ? err.message : 'Failed to load portfolio');
    } finally {
      setLoading(false);
    }
  };

  // Load the portfolio whenever the user becomes authenticated.
  useEffect(() => {
    if (authenticated) {
      void refreshPortfolio();
    } else {
      // Reset product state when signed out.
      setPartnerName(null);
      setPortfolio(null);
      setSavedSchools([]);
      setDraftState(new Map());
      setLoadError(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authenticated]);

  const addToDraft = (natEmis: number, source: 'manual' | 'spreadsheet') => {
    setDraftState((prev) => {
      const next = new Map(prev);
      next.set(natEmis, { nat_emis: natEmis, status: 'added', source });
      return next;
    });
  };

  const removeFromDraft = (natEmis: number) => {
    setDraftState((prev) => {
      const next = new Map(prev);
      next.delete(natEmis);
      return next;
    });
  };

  const setDraft = (schools: SaveSchoolInput[]) => {
    setDraftState(new Map(schools.map((s) => [s.nat_emis, s])));
  };

  // M4-R21K (D-01): additive merge. Preserves existing draft entries and adds
  // only the new nat_emis. This is the fix for the import-wipes-manual-draft
  // data-loss defect.
  const mergeIntoDraft = (schools: SaveSchoolInput[]) => {
    setDraftState((prev) => {
      const next = new Map(prev);
      for (const s of schools) {
        if (!next.has(s.nat_emis)) {
          next.set(s.nat_emis, s);
        }
      }
      return next;
    });
  };

  const clearDraft = () => {
    setDraftState(new Map());
  };

  const value = useMemo<PartnerSessionContextValue>(
    () => ({
      partnerName,
      portfolio,
      savedSchools,
      draft,
      loading,
      loadError,
      refreshPortfolio,
      addToDraft,
      removeFromDraft,
      setDraft,
      mergeIntoDraft,
      clearDraft,
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      partnerName,
      portfolio,
      savedSchools,
      draft,
      loading,
      loadError,
    ],
  );

  return (
    <PartnerSessionContext.Provider value={value}>
      {children}
    </PartnerSessionContext.Provider>
  );
}

export function usePartnerSession(): PartnerSessionContextValue {
  const ctx = useContext(PartnerSessionContext);
  if (!ctx) {
    throw new Error(
      'usePartnerSession must be used within a PartnerSessionProvider',
    );
  }
  return ctx;
}
